/**
 * Bootstrap virtual-web session from an existing platform session_id.
 * Used after login via Login modal (platform-core) to validate, create virtual session, and apply state.
 */
import i18n from 'i18n';
import { normalizeSupportedLanguage } from 'shared/utils/supportedLanguage';
import { setActiveVirtualSessionId } from 'shared/api/http-client';
import {
  validatePlatformSession,
  createVirtualSessionId,
  fetchVirtualSession,
} from './api';
import type { SessionUser } from './types';

export type Dispatch = (action: { type: string; payload?: unknown }) => void;

/**
 * Runs the same flow as useBootstrapSession when `s=` is present: validate platform session,
 * create virtual-core session (idempotent), fetch session, then apply to app (LocalStorage + dispatch).
 */
export async function bootstrapFromPlatformSessionId(
  sessionId: string,
  dispatch: Dispatch
): Promise<SessionUser> {
  const userData = await validatePlatformSession({ sessionId });
  const virtualSessionId = await createVirtualSessionId({
    platformSessionId: sessionId,
    userData,
  });

  setActiveVirtualSessionId(virtualSessionId);

  const session = await fetchVirtualSession({ virtualSessionId });

  if (session?.language && i18n?.changeLanguage) {
    i18n.changeLanguage(normalizeSupportedLanguage(session.language));
  }
  dispatch({ type: 'SET_USER', payload: session });

  return session;
}
